/**
 * InstrumentValues: an instrument's points with their latest reading and its
 * quality (web brief U7, per the platform's quality semantics in
 * lib/iot/reading-quality): valid shown normally, suspect marked uncertain,
 * and a point with no recent value shown as such, since a bad read stores no
 * row. Polled every 5 s while open.
 *
 * A page at a time: an instrument can carry hundreds of points (an auto-pour
 * station's controller, about 400), and `GET /v1/things/{id}/tags` answers 50
 * unless asked, so the list used to stop at the first 50 by name. The page
 * says which points it shows of how many, steps forward and back, and filters
 * by name on the server (`q`). Only the page's points are polled, in one
 * batched read (`POST /v1/iot/tags:latest`) rather than one read per point.
 *
 * Each point shows its own poll interval, deadband and heartbeat, and edits
 * them inline with whether it is read at all (`PATCH /v1/iot/tags/{id}`, like
 * the gateway sheet's tag rows). The server pushes the gateway its config on
 * save. Changing a point needs the member role; the server's refusal is shown
 * on the row.
 *
 * Each value says how long ago it arrived, from the reading's own time. The
 * clock is the readings' fetch, so the ages move with each 5 s refresh, with
 * no timer of their own. A point with a heartbeat is re-sent while it holds
 * steady, so it goes stale only after the heartbeat (lib/iot/reading-quality).
 * A point that is not read (`acquire` false) says so, never "No recent value".
 *
 * A failed read of the values (2026-10-30) no longer stops the clock: the ages
 * and staleness move on with each failed try too, and a line says the values are
 * from the last read that worked, with "Try again". A failed read of the points
 * offers "Try again" in place of "close and open again".
 *
 * InstrumentValuesView is pure (drawn in tests without providers);
 * InstrumentValues holds the page, the filter, the reads and the save.
 */

import { useEffect, useId, useMemo, useState } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Pencil } from "lucide-react";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { Input } from "@/lib/ui/input";
import { cn } from "@/lib/ui/cn";
import { tags as tagsApi, things, type ThingTag } from "@/lib/api";
import {
  HEARTBEAT_SEC_MAX,
  POINTS_PAGE_SIZE,
  POLL_SEC_MAX,
  durationShort,
  heartbeatDefault,
  latestByTag,
  pointRateBody,
  pointRateForm,
  pointSaveError,
  readingAge,
  type PointLatest,
  type PointRateEdit,
  type PointRateForm,
} from "@/lib/iot/instrument-points";
import { readingQuality } from "@/lib/iot/reading-quality";
import { usePointsPage } from "@/lib/iot/use-points-page";
import { valuesReadFailing } from "@/lib/iot/gateway-live";
import { PointsFilter, PointsPager } from "@/components/iot/PointsPaging";
import { ValuesReadFailed } from "@/components/iot/ValuesReadFailed";

export interface InstrumentValuesViewProps {
  state: "loading" | "failed" | "ready";
  /** The page's points. */
  items: ThingTag[];
  /** Every point that matches the filter. */
  total: number;
  offset: number;
  /** The name filter as typed. */
  filter: string;
  /** Latest reading by tag id; a point missing from it has none yet. */
  latest: Record<string, PointLatest | undefined>;
  /** The clock the ages and staleness are read at (epoch ms); now when absent. */
  now?: number;
  onFilter: (text: string) => void;
  onPage: (offset: number) => void;
  /** Save one point's poll, deadband, heartbeat or acquire; rejects with the server's error. Each point offers an edit when set. */
  onSavePoint?: (tagId: string, body: PointRateEdit) => Promise<void>;
  /** Read the points again after a failed read (a "Try again" button); without it the text says to reopen. */
  onRetry?: () => void;
}

export function InstrumentValuesView(p: InstrumentValuesViewProps) {
  // The filter stays while one is typed, so clearing it never strands the person on a narrowed list.
  const filterable = p.total > POINTS_PAGE_SIZE || p.filter.trim() !== "";
  return (
    <div className="flex flex-col gap-1.5">
      {filterable && <PointsFilter value={p.filter} onChange={p.onFilter} />}
      {p.state === "loading" ? (
        <p className="flex items-center gap-1.5 text-[11px] text-on-surface-muted">
          <Loader2 className="size-3 animate-spin" /> Loading points…
        </p>
      ) : p.state === "failed" ? (
        <p role="alert" className="flex flex-wrap items-center gap-2 text-[11px] text-danger">
          {p.onRetry ? "The points could not be read." : "The points could not be read. Close and open the values again to retry."}
          {p.onRetry && (
            <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[11px]" onClick={p.onRetry}>
              Try again
            </Button>
          )}
        </p>
      ) : p.items.length === 0 && p.offset > 0 ? (
        // Past the end: points were removed while this page was open.
        <p className="flex items-center gap-2 text-[11px] text-on-surface-muted">
          No points on this page.
          <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[11px]" onClick={() => p.onPage(0)}>
            First page
          </Button>
        </p>
      ) : p.items.length === 0 ? (
        <p className="text-[11px] text-on-surface-muted">
          {p.filter.trim() ? `No point matches “${p.filter.trim()}”.` : "No points."}
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-border/60">
          {p.items.map((t) => (
            <PointRow
              key={t.id}
              tag={t}
              latest={p.latest[t.id]}
              now={p.now}
              onSave={p.onSavePoint ? (body) => p.onSavePoint!(t.id, body) : undefined}
            />
          ))}
        </ul>
      )}
      {p.state === "ready" && p.items.length > 0 && (
        <PointsPager offset={p.offset} shown={p.items.length} total={p.total} onPage={p.onPage} />
      )}
    </div>
  );
}

function PointRow({
  tag: t,
  latest,
  now,
  onSave,
}: {
  tag: ThingTag;
  latest?: PointLatest;
  now?: number;
  onSave?: (body: PointRateEdit) => Promise<void>;
}) {
  // A point that is not read keeps its last value as history: never "No recent value".
  const off = t.acquire === false;
  const q = readingQuality(latest, t.poll_sec, now, t.max_silence_sec);
  const age = q.state === "none" ? null : readingAge(latest?.time, now);
  const metric = t.semantic_tag?.trim() || t.name;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<PointRateForm>(() => pointRateForm(t));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // "Saved; sent to the gateway." stays a few seconds.
  useEffect(() => {
    if (!saved) return;
    const timer = setTimeout(() => setSaved(false), 5000);
    return () => clearTimeout(timer);
  }, [saved]);

  const open = () => {
    setForm(pointRateForm(t));
    setError(null);
    setSaved(false);
    setEditing((v) => !v);
  };

  const save = async () => {
    if (!onSave) return;
    const r = pointRateBody(t, form);
    if ("error" in r) {
      setError(r.error);
      return;
    }
    if (Object.keys(r.body).length === 0) {
      setEditing(false);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSave(r.body);
    } catch (err) {
      setBusy(false);
      setError(pointSaveError(err));
      return;
    }
    setBusy(false);
    setEditing(false);
    setSaved(true);
  };

  return (
    <li className="flex flex-col gap-1 py-1 text-xs">
      <div className="flex items-center gap-2">
        <span className="truncate font-mono text-on-surface">{metric}</span>
        <PointRate
          pollSec={t.poll_sec}
          deadband={t.deadband}
          heartbeatSec={t.max_silence_sec}
          heartbeatOwn={t.max_silence_own}
        />
        <span className="ml-auto flex items-center gap-1.5">
          {q.state === "none" ? (
            <span className="text-[10px] text-on-surface-muted">no data yet</span>
          ) : (
            <span
              className={cn(
                "font-mono font-semibold",
                off ? "text-on-surface-muted" : q.state === "stale" ? "text-on-surface-muted line-through" : "text-on-surface",
              )}
            >
              {latest?.value != null ? formatValue(latest.value) : "no value"}
              {t.unit ? ` ${t.unit}` : ""}
            </span>
          )}
          {age && latest?.time && (
            <span className="shrink-0 text-[10px] text-on-surface-muted" title={`Latest reading: ${absoluteTime(latest.time)}`}>
              {age}
            </span>
          )}
          {off ? (
            <Badge
              variant="secondary"
              className="text-[9px]"
              title="The gateway leaves this point out, so it is not read. Its history is kept; edit it to read it again."
            >
              Not read
            </Badge>
          ) : (
            q.badge && (
              <Badge variant={q.state === "stale" ? "danger" : "warning"} className="text-[9px]" title={q.hint}>
                {q.badge}
              </Badge>
            )
          )}
          {onSave && (
            <button
              type="button"
              onClick={open}
              aria-expanded={editing}
              className="text-on-surface-muted hover:text-on-surface"
              title="Edit the poll interval, deadband, heartbeat and whether the point is read"
              aria-label={`Edit poll, deadband and heartbeat of ${metric}`}
            >
              <Pencil className="size-3.5" />
            </button>
          )}
        </span>
      </div>
      {editing && (
        <PointRateEditor
          metric={metric}
          form={form}
          heartbeatDefault={heartbeatDefault(t, form)}
          busy={busy}
          onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
          onSave={() => void save()}
          onCancel={() => {
            setEditing(false);
            setError(null);
          }}
        />
      )}
      {error && (
        <p role="alert" className="pl-1 text-[11px] text-danger">
          {error}
        </p>
      )}
      {saved && (
        <p role="status" className="pl-1 text-[11px] text-success">
          Saved; sent to the gateway.
        </p>
      )}
    </li>
  );
}

/**
 * A point's own poll interval and deadband, and its effective heartbeat, as the row shows them ("5 s", "±0.5",
 * "· hb 15 min").
 */
function PointRate({
  pollSec,
  deadband,
  heartbeatSec,
  heartbeatOwn,
}: {
  pollSec?: number | null;
  deadband?: number | null;
  heartbeatSec?: number | null;
  heartbeatOwn?: boolean;
}) {
  const hasPoll = pollSec != null && pollSec > 0;
  const hasDeadband = deadband != null && deadband > 0;
  return (
    <span className="flex shrink-0 items-center gap-1.5 font-mono text-[10px] text-on-surface-muted">
      {hasPoll && <span title={`Polled every ${pollSec} s`}>{pollSec} s</span>}
      {hasDeadband && <span title={`Deadband: published when it moves more than ${deadband}`}>±{deadband}</span>}
      {heartbeatSec != null && heartbeatSec > 0 && (
        <>
          {(hasPoll || hasDeadband) && <span aria-hidden>·</span>}
          <span
            title={`Heartbeat (${heartbeatOwn ? "its own" : "the default"}): re-sent at least every ${durationShort(heartbeatSec)} while it holds steady`}
          >
            hb {durationShort(heartbeatSec)}
          </span>
        </>
      )}
    </span>
  );
}

export interface PointRateEditorProps {
  metric: string;
  form: PointRateForm;
  /** What a blank heartbeat means (lib/iot/instrument-points heartbeatDefault). */
  heartbeatDefault: { hint: string; minutes: number | null };
  busy: boolean;
  onChange: (patch: Partial<PointRateForm>) => void;
  onSave: () => void;
  onCancel: () => void;
}

/**
 * One point's inline poll interval, deadband and heartbeat fields and its read switch, with Save and Cancel (the
 * gateway sheet's tag-edit look). The line under them says what a blank heartbeat means.
 */
export function PointRateEditor(p: PointRateEditorProps) {
  const pollId = useId();
  const deadbandId = useId();
  const heartbeatId = useId();
  return (
    <div className="flex flex-wrap items-center gap-2 pl-1">
      <label htmlFor={pollId} className="flex items-center gap-1 text-[10px] text-on-surface-muted">
        poll (s)
        <Input
          id={pollId}
          type="number"
          min={1}
          max={POLL_SEC_MAX}
          step={1}
          value={p.form.poll}
          onChange={(e) => p.onChange({ poll: e.target.value })}
          placeholder="—"
          aria-label={`Poll interval of ${p.metric}, seconds`}
          className="h-7 w-20 text-xs"
        />
      </label>
      <label htmlFor={deadbandId} className="flex items-center gap-1 text-[10px] text-on-surface-muted">
        deadband
        <Input
          id={deadbandId}
          type="number"
          min={0}
          step="any"
          value={p.form.deadband}
          onChange={(e) => p.onChange({ deadband: e.target.value })}
          placeholder="none"
          aria-label={`Deadband of ${p.metric}`}
          title="Publish only when the value moves more than this; empty or 0 publishes every read"
          className="h-7 w-20 text-xs"
        />
      </label>
      <label htmlFor={heartbeatId} className="flex items-center gap-1 text-[10px] text-on-surface-muted">
        heartbeat (min)
        <Input
          id={heartbeatId}
          type="number"
          min={1}
          max={HEARTBEAT_SEC_MAX / 60}
          step="any"
          value={p.form.heartbeat}
          onChange={(e) => p.onChange({ heartbeat: e.target.value })}
          placeholder={p.heartbeatDefault.minutes != null ? String(p.heartbeatDefault.minutes) : "none"}
          aria-label={`Heartbeat of ${p.metric}, minutes`}
          title="Re-send a value the deadband holds back once the point has been silent this long; empty uses the default"
          className="h-7 w-20 text-xs"
        />
      </label>
      <label
        className="flex items-center gap-1 text-[10px] text-on-surface-muted"
        title="Off leaves the point out of the gateway's config: it keeps its history but is not read"
      >
        <input
          type="checkbox"
          className="size-3.5 accent-brand"
          checked={p.form.acquire}
          onChange={(e) => p.onChange({ acquire: e.target.checked })}
          aria-label={`Read ${p.metric}`}
        />
        read this point
      </label>
      <Button size="sm" className="h-7" onClick={p.onSave} disabled={p.busy}>
        {p.busy ? <Loader2 className="size-3.5 animate-spin" /> : "Save"}
      </Button>
      <Button size="sm" variant="ghost" className="h-7" onClick={p.onCancel} disabled={p.busy}>
        Cancel
      </Button>
      <p className="w-full text-[10px] text-on-surface-muted">{p.heartbeatDefault.hint}</p>
    </div>
  );
}

/** `name` names the instrument in the failure note's button (the drawer passes it). */
export function InstrumentValues({ instrumentId, name }: { instrumentId: string; name?: string }) {
  const qc = useQueryClient();
  const { filter, setFilter, query, offset, setOffset } = usePointsPage();

  const tagsQ = useQuery({
    queryKey: ["instrument-tags", instrumentId, query, offset],
    queryFn: () => things.tags(instrumentId, { limit: POINTS_PAGE_SIZE, offset, q: query }),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
  const list = useMemo(() => tagsQ.data?.items ?? [], [tagsQ.data]);
  const ids = useMemo(() => list.map((t) => t.id), [list]);
  const latestQ = useQuery({
    queryKey: ["instrument-latest", instrumentId, ids.join(",")],
    enabled: ids.length > 0,
    refetchInterval: 5000,
    // A new page or filter keeps the values on screen until its own read lands (no "no data yet" flash).
    placeholderData: keepPreviousData,
    queryFn: async () => latestByTag((await tagsApi.batchLatest(ids)).items),
  });

  // The server pushes the gateway its config; the list reads the saved values back.
  const savePoint = async (tagId: string, body: PointRateEdit) => {
    await tagsApi.update(tagId, body);
    void qc.invalidateQueries({ queryKey: ["instrument-tags", instrumentId] });
  };

  // The rows' clock is the last read of the values, worked or failed: it moves on every 5 s try (and redraws the
  // rows even when no value changed), so the ages and staleness need no timer of their own, and a dead link
  // turns the values stale instead of showing "just now" for ever.
  const clock = Math.max(latestQ.dataUpdatedAt, latestQ.errorUpdatedAt);

  return (
    <div className="flex flex-col gap-1.5">
      {valuesReadFailing(latestQ) && (
        <ValuesReadFailed
          shownAt={latestQ.data ? latestQ.dataUpdatedAt : 0}
          onRetry={() => latestQ.refetch()}
          subject={name ? `the values of ${name}` : "the values"}
        />
      )}
      <InstrumentValuesView
        state={tagsQ.data ? "ready" : tagsQ.isError ? "failed" : "loading"}
        items={list}
        total={tagsQ.data?.total ?? 0}
        offset={tagsQ.data?.offset ?? offset}
        filter={filter}
        latest={latestQ.data ?? {}}
        now={clock || undefined}
        onFilter={setFilter}
        onPage={setOffset}
        onSavePoint={savePoint}
        onRetry={() => void tagsQ.refetch()}
      />
    </div>
  );
}

/** A reading's time in full, in the browser's zone, for the age's tooltip. */
function absoluteTime(iso: string): string {
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "medium" }) : iso;
}

function formatValue(v: number): string {
  if (!Number.isFinite(v)) return String(v);
  return Math.abs(v) >= 100 || Number.isInteger(v) ? String(Math.round(v * 100) / 100) : v.toFixed(2);
}
